import * as React from "react";
import { DayPicker, DayPickerSingleProps, DayPickerRangeProps, DateRange } from "react-day-picker";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";
import "react-day-picker/style.css";
import moment from "moment";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";

export interface DateInputProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  id: string;
  placeholder: string;
  prefix?: string;
  minDate?: Date;
  date?: Date;
  setDate: (date: Date | DateRange) => void;
  popupClassName: string;
  showError?: boolean;
  isFocus?: boolean;
  mode?: "single" | "range";
  dateRange?: DateRange;
}

const DateInput = React.forwardRef<HTMLDivElement, DateInputProps>(
  (
    {
      className,
      asChild = false,
      id,
      placeholder,
      prefix,
      minDate,
      date,
      setDate,
      popupClassName,
      children,
      showError = false,
      isFocus = false,
      mode = 'single',
      dateRange,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "div";

    // Remove the 9-day logic - calendar should show current month naturally
    const [isOpen, setIsOpen] = React.useState(false);
    const [month, setMonth] = React.useState(
      moment().toDate()
    );
    const [isMobileView, setIsMobileView] = React.useState(false);
  
    React.useEffect(() => {
      const handleResize = () => {
        setIsMobileView(window.innerWidth <= 768);
      };
  
      window.addEventListener("resize", handleResize);
      handleResize();
  
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, []);

    React.useEffect(() => {
      setIsOpen(isFocus);
    }, [isFocus]);

    // Removed dynamic style injection to avoid React strict mode warnings
    // Styles are now in the main CSS file

    // Handle day selection
    const handleDaySelect = (day: Date | undefined) => {
      if (!day) return;
      
      if (mode === 'single') {
        setDate(day);
        setIsOpen(false);
      } else if (mode === 'range') {
        // If we're in range mode, update the range
        if (!dateRange || !dateRange.from) {
          // First click - set the from date
          setDate({ from: day, to: undefined });
        } else if (dateRange.from && !dateRange.to && day >= dateRange.from) {
          // Ensure start date button is enabled
          setDate({ from: dateRange.from, to: day });
          setIsOpen(true);
        } else {
          // Allow starting a new selection with this day as the from date
          setDate({ from: day, to: undefined });
        }
      }
    };

    // Function to determine if a day is the pickup date
    const isPickupDate = (day: Date) => {
      if (!dateRange || !dateRange.from) return false;
      return day.getDate() === dateRange.from.getDate() && 
             day.getMonth() === dateRange.from.getMonth() && 
             day.getFullYear() === dateRange.from.getFullYear();
    };

    const disabledDate = (current: Date) => {
      // Only disable dates before today - no 9-day minimum
      return moment(current) < moment().startOf('day');
    };

    return (
      <Comp
        className={cn(
          `border-[1px] border-gray-300 rounded-md p-2 cursor-pointer ${isOpen ? "border-green-600 border-2" : (showError ? "border-red-500" : "")}`,
          className
        )}
        ref={ref}
        {...props}
      >
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger className="flex flex-row w-full items-center min-w-0">
            {prefix == null ? <></> : <img src={prefix} className="flex-shrink-0 w-4 h-4" />}
            <span
              id={id}
              className={`ml-2 outline-0 flex-1 min-w-0 text-center ${date == undefined ? "text-gray-400" : "text-black font-bold"}`}
              onClick={() => setIsOpen(true)}
              title={date ? moment(date).format("MMM DD, yyyy") : placeholder}
            >
              {date == undefined
                ? placeholder
                : moment(date).format("MMM DD, yyyy")}
            </span>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            avoidCollisions={true}
            collisionPadding={16}
            className={cn("w-max mt-2", popupClassName)}
          >
            <div className="absolute z-10 left-6 right-6 top-9 flex flex-row justify-between">
              <div
                className="cursor-pointer"
                onClick={() =>
                  setMonth(moment(month).subtract(1, "month").toDate())
                }
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-gray-400" />
              </div>
              <div
                className="cursor-pointer"
                onClick={() => setMonth(moment(month).add(1, "month").toDate())}
              >
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="text-gray-400"
                />
              </div>
            </div>
            {mode === 'single' ? (
              <DayPicker
                ISOWeek
                hideNavigation
                onMonthChange={setMonth}
                onDayClick={handleDaySelect}
                selected={date}
                mode="single"
                numberOfMonths={isMobileView ? 1 : 2}
                disabled={disabledDate}
                defaultMonth={moment().toDate()}
                month={month}
                required={false}
              />
            ) : (
              <DayPicker
                ISOWeek
                hideNavigation
                onMonthChange={setMonth}
                onDayClick={handleDaySelect}
                selected={dateRange}
                mode="range"
                numberOfMonths={isMobileView ? 1 : 2}
                defaultMonth={moment().toDate()}
                month={month}
                required={false}
                modifiers={{
                  highlighted: dateRange?.from ? [dateRange.from] : undefined
                }}
                modifiersClassNames={{
                  highlighted: 'pickup-date-highlight'
                }}
                onSelect={(range) => {
                  if (range) {
                    setDate(range);
                    if (range.from && range.to) {
                      setIsOpen(false);
                    }
                  }
                }}
                disabled={disabledDate}
              />
            )}
          </PopoverContent>
        </Popover>
      </Comp>
    );
  }
);

DateInput.displayName = "DateInput";

export { DateInput };
